import { useState, useRef, useEffect, useCallback } from "react";
import { Building2, ChevronDown, Check } from "lucide-react";
import { useAuthUser, useAuthActions } from "../lib/auth";

/**
 * Dropdown for switching between centers in a multi-center organization.
 * Hidden when the tenant has only one center.
 */
export function CenterSwitcher({ align = "left" }: { align?: "left" | "right" }) {
  const { centers, selectedCenterId } = useAuthUser();
  const { selectCenter } = useAuthActions();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // ESC key to close and restore focus
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  // Hide when single center (no switching needed)
  if (centers.length <= 1) return null;

  const selectedCenter = centers.find((c) => c.id === selectedCenterId);
  const label = selectedCenter?.name ?? "Select Center";

  function handleSelect(centerId: number) {
    selectCenter(centerId);
    setOpen(false);
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
        className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-all duration-200 bg-surface2/50 text-muted border border-border/30 hover:bg-surface2 hover:text-foreground"
      >
        <Building2 size={14} className="shrink-0" />
        <span className="max-w-[140px] truncate">{label}</span>
        <ChevronDown size={12} className={`shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div role="menu" className={`absolute ${align === "right" ? "right-0" : "left-0"} top-full mt-2 w-56 rounded-lg bg-surface py-1 shadow-soft border border-border/20 z-50`}>
          {centers.map((center) => (
            <button
              key={center.id}
              role="menuitem"
              onClick={() => handleSelect(center.id)}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-surface2 transition-colors"
            >
              <Check size={14} className={selectedCenterId === center.id ? "text-accent" : "invisible"} />
              <span className="truncate">{center.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Inline variant for mobile nav — renders as a list of selectable items
 * rather than a dropdown.
 */
export function CenterSwitcherInline({ onSelect }: { onSelect?: () => void }) {
  const { centers, selectedCenterId } = useAuthUser();
  const { selectCenter } = useAuthActions();

  if (centers.length <= 1) return null;

  function handleSelect(centerId: number) {
    selectCenter(centerId);
    onSelect?.();
  }

  return (
    <div>
      <span className="mb-1 px-3 text-xs font-medium uppercase tracking-wider text-muted">
        Center
      </span>
      {centers.map((center) => (
        <button
          key={center.id}
          onClick={() => handleSelect(center.id)}
          className={`flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-all ${
            selectedCenterId === center.id
              ? "bg-surface2 text-foreground"
              : "text-muted hover:bg-surface2/50 hover:text-foreground"
          }`}
        >
          <Building2 size={14} className="shrink-0" />
          <span className="truncate">{center.name}</span>
        </button>
      ))}
    </div>
  );
}
